import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { c } from 'ttag';
import { KeyboardKey } from '@standardnotes/ui-services';
import { useCallback, useRef, useState } from 'react';
import SearchOptions from '@/Components/SearchOptions/SearchOptions';
import SearchFilterSheet from '@/Components/SearchOptions/SearchFilterSheet';
import { MutuallyExclusiveMediaQueryBreakpoints, useMediaQuery } from '@/Hooks/useMediaQuery';
import Icon from '../Icon/Icon';
import DecoratedInput from '../Input/DecoratedInput';
import { observer } from 'mobx-react-lite';
import ClearInputButton from '../ClearInputButton/ClearInputButton';
import { ElementIds } from '@/Constants/ElementIDs';
import { classNames } from '@standardnotes/snjs';
const SearchFilterButton = ({ activeFilterCount, isOpen, onClick }) => (_jsxs("button", { type: "button", className: classNames('relative flex flex-shrink-0 cursor-pointer border-0 bg-transparent p-0', isOpen ? 'text-info' : 'text-passive-1 hover:text-info'), onMouseDown: (event) => event.preventDefault(), onClick: onClick, "aria-label": "Search filters", "aria-expanded": isOpen, children: [_jsx(Icon, { type: "tune", className: "h-4.5 w-4.5" }), activeFilterCount > 0 && (_jsx("span", { className: "absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-info px-0.5 text-[10px] font-semibold leading-none text-info-contrast", children: activeFilterCount }))] }));
const SearchBar = ({ itemListController, searchOptionsController, hideOptions = false, showSearchEnhancements = false, }) => {
    const searchBarRef = useRef(null);
    const searchInputRef = useRef(null);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const isMobileScreen = useMediaQuery(MutuallyExclusiveMediaQueryBreakpoints.sm);
    const { noteFilterText, setNoteFilterText, clearFilterText, onFilterEnter } = itemListController;
    const { activeSearchFilterCount } = searchOptionsController;
    const onNoteFilterTextChange = useCallback((text) => {
        setNoteFilterText(text);
    }, [setNoteFilterText]);
    const onNoteFilterKeyUp = useCallback((e) => {
        if (e.key === KeyboardKey.Enter) {
            onFilterEnter();
        }
    }, [onFilterEnter]);
    const onClearSearch = useCallback(() => {
        var _a;
        clearFilterText();
        (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [clearFilterText]);
    const toggleFilterPanel = useCallback(() => {
        setIsFilterPanelOpen((current) => !current);
    }, []);
    const closeFilterPanel = useCallback(() => {
        setIsFilterPanelOpen(false);
    }, []);
    const searchOptionsVisibilityClass = (() => {
        if (hideOptions) {
            return 'hidden';
        }
        if (showSearchEnhancements) {
            if (isMobileScreen) {
                return 'hidden';
            }
            return isFilterPanelOpen ? 'flex' : 'hidden';
        }
        if (!noteFilterText) {
            return 'hidden group-focus-within:flex';
        }
        return undefined;
    })();
    const rightDecorations = showSearchEnhancements
        ? [
            _jsxs("div", { className: "flex items-center gap-1", children: [noteFilterText && _jsx(ClearInputButton, { onClick: onClearSearch }), _jsx(SearchFilterButton, { activeFilterCount: activeSearchFilterCount, isOpen: isFilterPanelOpen, onClick: toggleFilterPanel })] }, "search-actions"),
        ]
        : [noteFilterText && _jsx(ClearInputButton, { onClick: onClearSearch }, "clear-search")].filter(Boolean);
    return (_jsxs("div", { className: "group pb-0.5 pt-3", role: "search", ref: searchBarRef, children: [_jsx(DecoratedInput, { autocomplete: false, id: ElementIds.SearchBar, className: {
                    container: 'px-1',
                    input: 'text-base placeholder:text-passive-0 lg:text-sm',
                }, placeholder: c('B2.NavSharedUI.Placeholder').t `Search...`, value: noteFilterText, ref: searchInputRef, onChange: onNoteFilterTextChange, onKeyUp: onNoteFilterKeyUp, left: [_jsx(Icon, { type: "search", className: "mr-1 h-4.5 w-4.5 flex-shrink-0 text-passive-1" })], right: rightDecorations, roundedFull: true }), _jsx("div", { className: classNames('animate-fade-from-top w-full', searchOptionsVisibilityClass), children: _jsx(SearchOptions, { searchOptions: searchOptionsController, showSearchEnhancements: showSearchEnhancements }) }), showSearchEnhancements && isMobileScreen && (_jsx(SearchFilterSheet, { open: isFilterPanelOpen && !hideOptions, onClose: closeFilterPanel, searchOptions: searchOptionsController }))] }));
};
export default observer(SearchBar);
